// 音乐缓存管理工具（基于IndexedDB，最大500MB，LRU淘汰，持久化）
// 用法：import musicCache from './musicCache';

const DB_NAME = 'music-cache-db';
const DB_VERSION = 9; // 🔥 升级到 9：新增 audio-ranges / audio-metadata（音频 Range 缓存）
const STORE_NAME = 'music-files';
const META_STORE = 'meta';
const LYRICS_STORE = 'music-lrc';
const MAX_CACHE_SIZE = 500 * 1024 * 1024; // 500MB

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION); // 🔥 版本 8，新增 HLS 分片相关对象存储
    request.onupgradeneeded = (event) => {
      const db = request.result;
      console.log('🗃️ [音乐缓存] 数据库升级中，版本:', event.oldVersion, '->', event.newVersion);
      
      // 🔥 修复：确保所有必要的对象存储都存在
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
        console.log('🗃️ [音乐缓存] 创建 music-files 对象存储');
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE);
        console.log('🗃️ [音乐缓存] 创建 meta 对象存储');
      }
      
      // 🔥 新增：确保视频缓存相关的对象存储也存在（兼容性）
      if (!db.objectStoreNames.contains('video-files')) {
        db.createObjectStore('video-files');
        console.log('🗃️ [音乐缓存] 创建 video-files 对象存储');
      }
      if (!db.objectStoreNames.contains('partial-video-cache')) {
        db.createObjectStore('partial-video-cache');
        console.log('🗃️ [音乐缓存] 创建 partial-video-cache 对象存储');
      }
      
      // 🔥 新增：确保图集媒体缓存对象存储存在
      if (!db.objectStoreNames.contains('music-album')) {
        db.createObjectStore('music-album');
        console.log('🗃️ [音乐缓存] 创建 music-album 对象存储');
      }
      
      // 🔥 新增：确保歌词缓存对象存储存在
      if (!db.objectStoreNames.contains(LYRICS_STORE)) {
        db.createObjectStore(LYRICS_STORE);
        console.log('🗃️ [音乐缓存] 创建 music-lrc 对象存储');
      }

      // 🔥 新增（v8）：HLS 分片缓存与分片清单对象存储
      if (!db.objectStoreNames.contains('video-segments')) {
        db.createObjectStore('video-segments');
        console.log('🗃️ [音乐缓存] 创建 video-segments 对象存储');
      }
      if (!db.objectStoreNames.contains('video-seg-manifest')) {
        db.createObjectStore('video-seg-manifest');
        console.log('🗃️ [音乐缓存] 创建 video-seg-manifest 对象存储');
      }

      // 🔥 新增（v9）：音频 Range 缓存（in-line key，带索引）
      if (!db.objectStoreNames.contains('audio-ranges')) {
        const rangeStore = db.createObjectStore('audio-ranges', { keyPath: 'key' });
        rangeStore.createIndex('audioId', 'audioId', { unique: false });
        rangeStore.createIndex('cachedAt', 'cachedAt', { unique: false });
        console.log('🗃️ [音乐缓存] 创建 audio-ranges 对象存储');
      }
      if (!db.objectStoreNames.contains('audio-metadata')) {
        db.createObjectStore('audio-metadata', { keyPath: 'audioId' });
        console.log('🗃️ [音乐缓存] 创建 audio-metadata 对象存储');
      }
      
      console.log('🗃️ [音乐缓存] 数据库升级完成，当前对象存储:', Array.from(db.objectStoreNames));
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function getMeta(db) {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(META_STORE, 'readonly');
      const store = tx.objectStore(META_STORE);
      const req = store.get('lru');
      req.onsuccess = () => resolve(req.result || { order: [], size: 0, map: {} });
      req.onerror = () => {
        console.warn('🗃️ [音乐缓存] 获取元数据失败，返回默认值');
        resolve({ order: [], size: 0, map: {} });
      };
    } catch (error) {
      console.warn('🗃️ [音乐缓存] 创建元数据事务失败:', error);
      resolve({ order: [], size: 0, map: {} });
    }
  });
}

async function setMeta(db, meta) {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(META_STORE, 'readwrite');
      const store = tx.objectStore(META_STORE);
      store.put(meta, 'lru');
      tx.oncomplete = resolve;
      tx.onerror = () => {
        console.warn('🗃️ [音乐缓存] 设置元数据失败');
        resolve();
      };
    } catch (error) {
      console.warn('🗃️ [音乐缓存] 创建元数据写入事务失败:', error);
      resolve();
    }
  });
}

function getFileSize(blob) {
  return blob.size || 0;
}

function normalizeLyricsRecord(record) {
  if (!record) return null;
  if (typeof record === 'string') return record;
  if (typeof record.text === 'string') return record.text;
  return null;
}

async function getLyricsFromStore(key) {
  try {
    const db = await openDB();
    return await new Promise((resolve) => {
      try {
        const tx = db.transaction(LYRICS_STORE, 'readonly');
        const store = tx.objectStore(LYRICS_STORE);
        const req = store.get(key);
        req.onsuccess = () => resolve(normalizeLyricsRecord(req.result));
        req.onerror = () => {
          console.warn('📝 [歌词缓存] 获取失败:', key);
          resolve(null);
        };
      } catch (error) {
        console.warn('📝 [歌词缓存] 创建读取事务失败:', error);
        resolve(null);
      }
    });
  } catch (error) {
    console.warn('📝 [歌词缓存] 打开数据库失败:', error);
    return null;
  }
}

async function setLyricsToStore(key, lyricsText) {
  if (!lyricsText) return;
  try {
    const db = await openDB();
    await new Promise((resolve) => {
      try {
        const tx = db.transaction(LYRICS_STORE, 'readwrite');
        const store = tx.objectStore(LYRICS_STORE);
        store.put({
          text: lyricsText,
          updatedAt: Date.now(),
          length: lyricsText.length || 0
        }, key);
        tx.oncomplete = resolve;
        tx.onerror = () => {
          console.warn('📝 [歌词缓存] 写入失败:', key);
          resolve();
        };
      } catch (error) {
        console.warn('📝 [歌词缓存] 创建写入事务失败:', error);
        resolve();
      }
    });
  } catch (error) {
    console.warn('📝 [歌词缓存] 打开数据库失败:', error);
  }
}

async function deleteLyricsFromStore(key) {
  try {
    const db = await openDB();
    await new Promise((resolve) => {
      try {
        const tx = db.transaction(LYRICS_STORE, 'readwrite');
        const store = tx.objectStore(LYRICS_STORE);
        store.delete(key);
        tx.oncomplete = resolve;
        tx.onerror = () => {
          console.warn('📝 [歌词缓存] 删除失败:', key);
          resolve();
        };
      } catch (error) {
        console.warn('📝 [歌词缓存] 创建删除事务失败:', error);
        resolve();
      }
    });
  } catch (error) {
    console.warn('📝 [歌词缓存] 打开数据库失败:', error);
  }
}

const musicCache = {
  async get(key) {
    try {
      const db = await openDB();
      return new Promise((resolve) => {
        try {
          const tx = db.transaction(STORE_NAME, 'readonly');
          const store = tx.objectStore(STORE_NAME);
          const req = store.get(key);
          req.onsuccess = () => resolve(req.result || null);
          req.onerror = () => {
            console.warn('🗃️ [音乐缓存] 获取缓存失败:', key);
            resolve(null);
          };
        } catch (error) {
          console.warn('🗃️ [音乐缓存] 创建获取事务失败:', error);
          resolve(null);
        }
      });
    } catch (error) {
      console.warn('🗃️ [音乐缓存] 打开数据库失败:', error);
      return null;
    }
  },

  async set(key, blob) {
    try {
      const db = await openDB();
      let meta = await getMeta(db);
      const size = getFileSize(blob);
      
      // LRU: 如果已存在，先移除旧的
      if (meta.map[key]) {
        const idx = meta.order.indexOf(key);
        if (idx !== -1) meta.order.splice(idx, 1);
        meta.size -= meta.map[key];
        delete meta.map[key];
      }
      
      // LRU: 淘汰直到足够空间
      while (meta.size + size > MAX_CACHE_SIZE && meta.order.length > 0) {
        const oldest = meta.order.shift();
        await this.delete(oldest, db, meta); // 传递db和meta避免重复打开
      }
      
      // 存储文件
      await new Promise((resolve, reject) => {
        try {
          const tx = db.transaction(STORE_NAME, 'readwrite');
          const store = tx.objectStore(STORE_NAME);
          store.put(blob, key);
          tx.oncomplete = resolve;
          tx.onerror = () => {
            console.warn('🗃️ [音乐缓存] 存储文件失败:', key);
            resolve(); // 不抛出错误，继续执行
          };
        } catch (error) {
          console.warn('🗃️ [音乐缓存] 创建存储事务失败:', error);
          resolve(); // 不抛出错误，继续执行
        }
      });
      
      // 更新LRU
      meta.order.push(key);
      meta.map[key] = size;
      meta.size += size;
      await setMeta(db, meta);
      
    } catch (error) {
      console.error('🗃️ [音乐缓存] 缓存设置失败:', error);
      // 不抛出错误，让应用继续运行
    }
  },

  async delete(key, db = null, meta = null) {
    try {
      db = db || await openDB();
      meta = meta || await getMeta(db);
      
      // 删除文件
      await new Promise((resolve) => {
        try {
          const tx = db.transaction(STORE_NAME, 'readwrite');
          const store = tx.objectStore(STORE_NAME);
          store.delete(key);
          tx.oncomplete = resolve;
          tx.onerror = () => {
            console.warn('🗃️ [音乐缓存] 删除文件失败:', key);
            resolve();
          };
        } catch (error) {
          console.warn('🗃️ [音乐缓存] 创建删除事务失败:', error);
          resolve();
        }
      });
      
      // 更新LRU
      if (meta.map[key]) {
        const idx = meta.order.indexOf(key);
        if (idx !== -1) meta.order.splice(idx, 1);
        meta.size -= meta.map[key];
        delete meta.map[key];
        await setMeta(db, meta);
      }
    } catch (error) {
      console.error('🗃️ [音乐缓存] 删除缓存失败:', error);
    }
  },

  async clear() {
    try {
      const db = await openDB();
      await new Promise((resolve) => {
        try {
          const tx = db.transaction(STORE_NAME, 'readwrite');
          tx.objectStore(STORE_NAME).clear();
          tx.oncomplete = resolve;
          tx.onerror = () => {
            console.warn('🗃️ [音乐缓存] 清空缓存失败');
            resolve();
          };
        } catch (error) {
          console.warn('🗃️ [音乐缓存] 创建清空事务失败:', error);
          resolve();
        }
      });
      await setMeta(db, { order: [], size: 0, map: {} });
    } catch (error) {
      console.error('🗃️ [音乐缓存] 清空操作失败:', error);
    }
  },

  async getCacheSize() {
    try {
      const db = await openDB();
      const meta = await getMeta(db);
      return meta.size;
    } catch (error) {
      console.warn('🗃️ [音乐缓存] 获取缓存大小失败:', error);
      return 0;
    }
  },

  async getCacheKeys() {
    try {
      const db = await openDB();
      const meta = await getMeta(db);
      return meta.order.slice();
    } catch (error) {
      console.warn('🗃️ [音乐缓存] 获取缓存键失败:', error);
      return [];
    }
  },

  // 别名方法，兼容HybridAudioController
  async keys() {
    return await this.getCacheKeys();
  },

  async getLyrics(key) {
    if (!key) return null;
    return await getLyricsFromStore(key);
  },

  async setLyrics(key, lyricsText) {
    if (!key || !lyricsText) return;
    await setLyricsToStore(key, lyricsText);
  },

  async deleteLyrics(key) {
    if (!key) return;
    await deleteLyricsFromStore(key);
  }
};

export default musicCache; 